import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Drawer } from './ui/Drawer';
import { api } from '../lib/api';
import { useArInvoices } from '../hooks/useArInvoices';

const inputCn =
  'w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 ' +
  'bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500';

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label className="text-[11px] uppercase tracking-wide text-ink-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

export function PeppolSendDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ar = useArInvoices(wsId);
  const [arId, setArId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [supplierJpRegistration, setSupplierJpRegistration] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPeppolId, setCustomerPeppolId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(() => ar.data?.find((i) => i.id === arId) ?? null, [ar.data, arId]);

  const send = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('No AR invoice selected');
      const invoice = {
        invoiceNumber: selected.invoice_number,
        issueDate: selected.issue_date,
        dueDate: selected.due_date,
        currency: selected.currency,
        supplierName,
        supplierJpRegistration: supplierJpRegistration.trim() || undefined,
        customerName,
        customerPeppolId: customerPeppolId.trim() || undefined,
        lineItems: selected.line_items,
        subtotalCents: Number(selected.subtotal_cents),
        taxCents: Number(selected.tax_cents),
        totalCents: Number(selected.total_cents),
      };
      return (await api.post(`/workspaces/${wsId}/peppol/send`, { arInvoiceId: selected.id, invoice }))
        .data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['peppol', wsId] });
      qc.invalidateQueries({ queryKey: ['ar-invoices', wsId] });
      onOpenChange(false);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Send failed'),
  });

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('peppol.sendInvoice')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (!selected) {
                setError(t('peppol.errorPickInvoice'));
                return;
              }
              if (!supplierName.trim() || !customerName.trim()) {
                setError(t('peppol.errorPartiesRequired'));
                return;
              }
              send.mutate();
            }}
            disabled={send.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('peppol.send')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <FormField label={t('peppol.pickArInvoice')}>
          <select value={arId} onChange={(e) => setArId(e.target.value)} className={inputCn}>
            <option value="">—</option>
            {(ar.data ?? []).map((i) => (
              <option key={i.id} value={i.id}>
                {i.invoice_number} ({i.currency} · {i.status})
              </option>
            ))}
          </select>
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('peppol.supplierName')}>
            <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className={inputCn} />
          </FormField>
          <FormField label={t('peppol.supplierJpReg')}>
            <input
              value={supplierJpRegistration}
              onChange={(e) => setSupplierJpRegistration(e.target.value.toUpperCase())}
              placeholder="T1234567890123"
              className={inputCn}
            />
          </FormField>
          <FormField label={t('peppol.customerName')}>
            <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className={inputCn} />
          </FormField>
          <FormField label={t('peppol.customerPeppolId')}>
            <input
              value={customerPeppolId}
              onChange={(e) => setCustomerPeppolId(e.target.value)}
              placeholder="0151:1234567890123"
              className={inputCn}
            />
          </FormField>
        </div>
      </div>
    </Drawer>
  );
}

export function PeppolIngestDrawer({
  wsId,
  open,
  onOpenChange,
}: {
  wsId: string;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ublXml, setUblXml] = useState('');
  const [messageId, setMessageId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const ingest = useMutation({
    mutationFn: async () =>
      (await api.post(`/workspaces/${wsId}/peppol/ingest`, {
        ublXml,
        messageId: messageId.trim() || undefined,
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['peppol', wsId] });
      setUblXml('');
      setMessageId('');
      onOpenChange(false);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? err?.message ?? 'Ingest failed'),
  });

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('peppol.ingestUbl')}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (!ublXml.trim()) {
                setError(t('peppol.errorXmlRequired'));
                return;
              }
              ingest.mutate();
            }}
            disabled={ingest.isPending}
            className="px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
          >
            {t('peppol.ingest')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
            {error}
          </div>
        )}
        <FormField label={t('peppol.messageId')}>
          <input value={messageId} onChange={(e) => setMessageId(e.target.value)} className={inputCn} />
        </FormField>
        <FormField label={t('peppol.ublXml')}>
          <textarea
            value={ublXml}
            onChange={(e) => setUblXml(e.target.value)}
            rows={16}
            placeholder='<?xml version="1.0" encoding="UTF-8"?><Invoice ...'
            className={`${inputCn} font-mono text-[11px]`}
          />
        </FormField>
      </div>
    </Drawer>
  );
}
